import { useCallback, useEffect, useRef, useState } from "react";
import { registerAttachmentFilename, registerAttachmentVersion } from "@/components/chat/documentVersionEvents";
import { useAuth } from "@/context/AuthContext";
import { useOperations } from "@/context/OperationContext";
import type { SharePointDownloadResult } from "@/hooks/useChatAttachments";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { hasHandoff, makeHandoffMessage } from "@/lib/agentHandoff";
import { buildChatSessionPath } from "@/lib/chatNavigation";
import type { ActiveOperation } from "@/lib/services";
import { agentService, chatService } from "@/lib/services";
import type {
  AgentHandoff,
  AgentHandoffSuggestion,
  AgentRouteDecision,
  AgentRouteOutcome,
  ChatMessage,
  ChatSession,
  ExtensionAccessRequest,
  MessageAttachment,
  ReasoningStep,
  ReasoningSummaryEvent,
  RoutedAgent,
  SharePointDocumentMeta,
  SharePointItem,
  SourceMetadata,
  ToolEndEvent,
  ToolStartEvent,
} from "@/types";
import { isAttachmentErrorDetail } from "@/types/common";
import type { AskUserQuestion } from "@/types/task";

/** Throttled streaming update interval in milliseconds. */
const UPDATE_INTERVAL = 50;

/** Default error message shown to the user when a stream fails. */
const DEFAULT_ERROR_MSG = "Sorry, an error occurred while processing your message. Please try again.";

/** How long a first message waits for the auto-router before going to plain chat (the backend allows its LLM 10 s). */
const ROUTE_TIMEOUT_MS = 15_000;

/** Context management info emitted by the backend during streaming. */
export interface ContextInfo {
  context_window: number;
  max_output_tokens: number;
  tool_tokens: number;
  budget: number;
  prompt_tokens: number;
  percent_used: number;
  messages_loaded: number;
  messages_after_trim: number;
  groups_total: number;
  groups_pruned: number;
  groups_trimmed: number;
  summarized: boolean;
  model: string;
}

/** Artifact info collected from tool_end events during streaming. */
export interface StreamingArtifact {
  filename: string;
  url: string;
  is_image: boolean;
  mime_type: string;
  /** UUID of the ChatAttachment row created by the backend. */
  attachment_uuid?: string;
  /** Document version (1-based). Present when lineage tracking is active. */
  version?: number;
  /** UUID of the document lineage group. */
  lineage_uuid?: string;
  /** True when this artifact is a revision of a previously generated document. */
  is_revision?: boolean;
  /** True when the artifact is an HTML document for inline preview. */
  is_html?: boolean;
  /** Title of the HTML document (for display in cards/panels). */
  title?: string;
  /** The full HTML content (for immediate rendering without fetch). */
  html_content?: string;
}

// ─── Pure helpers for injectMissingArtifacts ─────────────────────────────────

/** Returns true if the artifact is already referenced in the content. */
export function isArtifactPresent(content: string, art: StreamingArtifact): boolean {
  // NOSONAR -- regex pattern matching with necessary branches
  if (content.includes(art.url)) return true;

  // Check if the filename appears in a markdown image/link pattern,
  // which would indicate the LLM referenced it by name (even if with a
  // different path like /mnt/data/). This prevents duplicate chips.
  const filenameEscaped = art.filename.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const filenamePattern = new RegExp(String.raw`(!?\[[^\]]*\]\([^)]*${filenameEscaped}[^)]*\))`);
  return filenamePattern.test(content);
}

/** Partition artifacts into missing images and missing files. */
export function classifyMissingArtifacts(
  content: string,
  artifacts: StreamingArtifact[],
): { missingImages: StreamingArtifact[]; missingFiles: StreamingArtifact[] } {
  const missingImages: StreamingArtifact[] = [];
  const missingFiles: StreamingArtifact[] = [];

  for (const art of artifacts) {
    // HTML artifacts are rendered as cards, not injected as markdown
    if (art.is_html) continue;
    if (isArtifactPresent(content, art)) continue;
    (art.is_image ? missingImages : missingFiles).push(art);
  }

  return { missingImages, missingFiles };
}

/** Build markdown for missing artifacts and append to content. */
export function appendArtifactMarkdown(
  content: string,
  missingImages: StreamingArtifact[],
  missingFiles: StreamingArtifact[],
): string {
  let appended = content;
  if (missingImages.length > 0) {
    appended += "\n\n";
    for (const img of missingImages) {
      appended += `![${img.filename}](${img.url})\n\n`;
    }
  }
  if (missingFiles.length > 0) {
    for (const file of missingFiles) {
      const label =
        file.is_revision && file.version ? `Download ${file.filename} (v${file.version})` : `Download ${file.filename}`;
      appended += `[${label}](${file.url})\n\n`;
    }
  }
  return appended.trimEnd();
}

/**
 * Scan the assistant's final content for artifact URLs and append any
 * missing artifact markdown (images and download links) as a fallback.
 * This handles the case where the LLM describes artifacts verbally instead
 * of including the inline markdown we provided in the tool result.
 */
export function injectMissingArtifacts(content: string, artifacts: StreamingArtifact[]): string {
  if (artifacts.length === 0) return content;

  const { missingImages, missingFiles } = classifyMissingArtifacts(content, artifacts);
  if (missingImages.length === 0 && missingFiles.length === 0) return content;

  return appendArtifactMarkdown(content, missingImages, missingFiles);
}

// ─── Pure helpers for sanitizeDownloadLinks ──────────────────────────────────

// Markdown links/images with an absolute URL (`http(s)://` or `//`); group 1 is
// `!` for images. The text and URL classes stop at brackets/parens/whitespace
// to keep matching linear.
const ABSOLUTE_LINK_RE = /(!?)\[([^[\]]*)\]\(((?:https?:)?\/\/[^()\s]*)\)/gi;
const DOWNLOAD_TEXT_RE = /^\W*download/i;

/** Percent-decode like Python's `unquote`: a malformed escape is kept as-is instead of throwing. */
function decodeUrl(url: string): string {
  return url.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
    try {
      return decodeURIComponent(run);
    } catch {
      return run;
    }
  });
}

/** Return true if a link's URL, URL file name or text points at this generated file. */
function linkNamesFile(file: { uuid: string; filename: string }, url: string, fileName: string, text: string): boolean {
  return url.includes(file.uuid) || file.filename === fileName || text.includes(file.filename);
}

/**
 * Repoint or unlink absolute links the LLM wrote for this turn's generated
 * files — it sometimes invents download URLs (e.g. an S3 bucket that does not
 * exist) instead of copying the attachment URL from the tool result. Links to
 * cited sources are kept. A link that names exactly one saved artifact (its
 * uuid in the URL, or its filename as the URL's last path segment or in the
 * link text) is rewritten to its attachment URL; otherwise a "Download…" link
 * is replaced by its text. Mirrors the backend's `_sanitize_download_links`,
 * which fixes the persisted copy of the message and only sees saved artifacts.
 */
export function sanitizeDownloadLinks(
  content: string,
  artifacts: StreamingArtifact[],
  sources: SourceMetadata[],
): string {
  const saved = artifacts.flatMap((art) =>
    art.attachment_uuid ? [{ uuid: art.attachment_uuid, filename: art.filename }] : [],
  );
  if (saved.length === 0) return content;

  const sourceUrls = new Set(sources.map((src) => src.url));
  return content.replace(ABSOLUTE_LINK_RE, (match, bang: string, text: string, url: string) => {
    if (bang || sourceUrls.has(url)) return match;
    const path = url.split(/[?#]/, 1)[0];
    const fileName = decodeUrl(path.slice(path.lastIndexOf("/") + 1));
    const named = new Set(saved.filter((file) => linkNamesFile(file, url, fileName, text)).map((file) => file.uuid));
    if (named.size === 1) return `[${text}](/chat/attachments/${[...named][0]}/file)`;
    if (DOWNLOAD_TEXT_RE.test(text)) return text;
    return match;
  });
}

// ─── Pure helpers for the SSE event handler ──────────────────────────────────

/** Convert a MIME type to a friendly file extension, e.g. "image/jpeg" → ".jpg". */
function mimeToExt(mime: string | undefined | null): string {
  if (!mime) return "";
  const map: Record<string, string> = {
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    "text/plain": ".txt",
    "text/csv": ".csv",
    "text/markdown": ".md",
    "application/json": ".json",
  };
  return map[mime] ?? `.${mime.split("/").pop() ?? mime}`;
}

/**
 * Convert a structured attachment error detail into a user-friendly message.
 * Each error_code maps to a distinct, specific message.
 */
export function formatAttachmentError(detail: unknown): string {
  if (!isAttachmentErrorDetail(detail)) return "Failed to upload file.";

  switch (detail.error_code) {
    case "unsupported_file_type": {
      const ext = mimeToExt(detail.mime_type);
      const imageExts = detail.allowed_image_types.map(mimeToExt).join(", ");
      const docExts = detail.allowed_document_types.map(mimeToExt).join(", ");
      const name = detail.filename ? `"${detail.filename}" ` : "";
      return (
        `${name}is not a supported file type (${ext}). ` +
        `Supported formats — images: ${imageExts}; documents: ${docExts}.`
      );
    }
    case "file_too_large": {
      const maxMb = (detail.max_size / (1024 * 1024)).toFixed(0);
      const actualMb = (detail.size / (1024 * 1024)).toFixed(1);
      return `File is too large (${actualMb} MB). Maximum size for ${detail.file_kind}s is ${maxMb} MB.`;
    }
    case "too_many_files":
      return `Too many files selected (${detail.count}). Maximum is ${detail.max} files per upload.`;
  }
}

/** Create a placeholder ChatMessage for errors or assistant responses. */
export function makeErrorMessage(sessionId: number, content: string): ChatMessage {
  return {
    id: Date.now() + 1,
    uuid: `error-${Date.now()}`,
    sessionId,
    role: "assistant",
    content,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Convert streaming artifacts into ``MessageAttachment`` objects so the
 * persisted-style message can render ``ArtifactCard`` / ``HtmlArtifactCard``
 * components immediately (without waiting for a page refresh that fetches
 * the real attachments from the API).
 */
export function artifactsToAttachments(artifacts: StreamingArtifact[]): MessageAttachment[] {
  return artifacts
    .filter((a) => !!a.attachment_uuid)
    .map((a) => ({
      uuid: a.attachment_uuid!,
      filename: a.filename,
      mimeType: a.mime_type,
      size: 0,
      createdAt: new Date().toISOString(),
      url: a.url,
      version: a.version ?? null,
      lineageUuid: a.lineage_uuid ?? null,
      isRevision: a.is_revision ?? false,
      origin: "generated" as const,
    }));
}

/** Create a ChatMessage for a finished assistant response. */
export function makeAssistantMessage(
  sessionId: number,
  content: string,
  sources: SourceMetadata[],
  reasoning?: ReasoningStep[],
  artifacts?: StreamingArtifact[],
  reasoningSummary?: string,
): ChatMessage {
  const attachments = artifacts?.length ? artifactsToAttachments(artifacts) : undefined;
  return {
    id: Date.now() + 1,
    uuid: `msg-${Date.now()}`,
    sessionId,
    role: "assistant",
    content,
    createdAt: new Date().toISOString(),
    ...(attachments && attachments.length > 0 && { attachments }),
    ...((sources.length > 0 || reasoningSummary) && {
      messageMetadata: {
        ...(sources.length > 0 && { sources }),
        ...(reasoningSummary && { reasoning_summary: reasoningSummary }),
      },
    }),
    ...(reasoning &&
      reasoning.length > 0 && {
        reasoningSteps: reasoning,
      }),
  };
}

// ─── Pure helpers for handleSend ─────────────────────────────────────────────

/** Build preliminary SharePoint metadata from pending file chips. */
export function buildPreliminaryMetas(files: SharePointItem[]): SharePointDocumentMeta[] | undefined {
  if (files.length === 0) return undefined;
  return files.map((f) => ({
    filename: f.name,
    url: f.web_url,
    size: f.size,
    mimeType: f.mime_type,
  }));
}

/** Create the temp user message shown immediately in the UI. */
export function makeTempUserMessage(
  sessionId: number,
  content: string,
  preliminaryMetas: SharePointDocumentMeta[] | undefined,
): ChatMessage {
  return {
    id: Date.now(),
    uuid: `temp-${Date.now()}`,
    sessionId,
    role: "user",
    content,
    messageMetadata: preliminaryMetas ? { sharepoint_documents: preliminaryMetas } : undefined,
    createdAt: new Date().toISOString(),
  };
}

/** Upload a batch of files and return their UUIDs (or undefined on failure).
 * When `aborted` is true the caller should stop the message send entirely —
 * the file itself is the problem (too large, wrong type, too many) and
 * sending without it would produce a confusing or incomplete AI response. */
export async function uploadAttachmentBatch(
  sessionUuid: string,
  files: File[],
  label: string,
  showToast: (msg: string, type: "error" | "warning" | "success" | "info") => void,
  setIsUploading: (v: boolean) => void,
): Promise<{ uuids: string[] | undefined; attachments: MessageAttachment[] | undefined; aborted: boolean }> {
  if (files.length === 0) return { uuids: undefined, attachments: undefined, aborted: false };
  try {
    setIsUploading(true);
    const uploadRes = await chatService.uploadAttachments(sessionUuid, files);
    return {
      uuids: uploadRes.data.map((att) => att.uuid),
      attachments: uploadRes.data,
      aborted: false,
    };
  } catch (err) {
    console.error(`Failed to upload ${label} attachments:`, err);
    // Surface the backend's structured error detail when available.
    let errorMsg = `Failed to upload ${label}. Sending message without them.`;
    if (err instanceof Error && "data" in err) {
      const data = (err as { data?: { detail?: unknown } }).data;
      if (data?.detail) {
        errorMsg = formatAttachmentError(data.detail);
      }
    }
    // Abort the send for 4xx errors — the file itself is invalid.
    // For network errors or 5xx, proceed without the attachment.
    const status = (err as { status?: number }).status;
    const aborted = typeof status === "number" && status >= 400 && status < 500;
    showToast(errorMsg, "error");
    return { uuids: undefined, attachments: undefined, aborted };
  } finally {
    setIsUploading(false);
  }
}

/** Resolve a session from the backend and update UI state. */
async function resolveNewSession(
  sessionUuid: string,
  setCurrentSession: (s: ChatSession) => void,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  setSidebarRefreshTrigger: React.Dispatch<React.SetStateAction<number>>,
  sessionRef: React.MutableRefObject<ChatSession | null>,
): Promise<ChatSession | null> {
  try {
    const sessionRes = await chatService.getSession(sessionUuid);
    const session = sessionRes.data;
    sessionRef.current = session;
    setCurrentSession(session);
    navigate(buildChatSessionPath(session.uuid, session.projectUuid), { replace: true });
    setSidebarRefreshTrigger((n) => n + 1);
    return session;
  } catch (err) {
    console.error("Failed to resolve session:", err);
    return null;
  }
}

/** Build an ActiveOperation record for tracking. */
export function buildOperationRecord(
  operationId: string,
  operationType: string,
  entityId: string,
  title: string,
): ActiveOperation {
  const now = new Date().toISOString();
  return {
    operation_id: operationId,
    operation_type: operationType,
    entity_id: entityId,
    status: "running",
    title,
    started_at: now,
    updated_at: now,
    user_id: 0,
  };
}

/** Update a temp user message's attachments in the message list (replace or append). */
export function updateMessageAttachments(
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  tempMessageUuid: string,
  newAttachments: MessageAttachment[],
  mode: "replace" | "append",
) {
  setMessages((prev) =>
    prev.map((m) => {
      if (m.uuid !== tempMessageUuid) return m;
      const merged = mode === "replace" ? newAttachments : [...(m.attachments || []), ...newAttachments];
      return { ...m, attachments: merged };
    }),
  );
}

/**
 * Pending files consumed from the attachments hook, passed into handleSend.
 */
export interface ConsumedFiles {
  imageFiles: File[];
  documentFiles: File[];
  sharepointFiles: SharePointItem[];
}

// ---------------------------------------------------------------------------
// Auto-routing (MHS-627)
// ---------------------------------------------------------------------------

/** The inline routing row for a new chat's first message. */
export type RouteState =
  | { kind: "routing"; anchorUuid: string }
  | { kind: "suggest"; anchorUuid: string; decision: AgentRouteDecision }
  | {
      kind: "routed";
      anchorUuid: string;
      agent: RoutedAgent;
      decision: AgentRouteDecision;
      sessionUuid: string | null;
    };

/** A first message and its attachments, as handleSend collected them. */
interface FirstSend {
  messageContent: string;
  imageFiles: File[];
  documentFiles: File[];
  tempMessageUuid: string;
  sharepointMetas: SharePointDocumentMeta[] | undefined;
  sharepointContentText: string | undefined;
  sharepointSlideImages: string[] | undefined;
}

/**
 * The routing row to show now: a routed row belongs to its chat; the routing and
 * suggestion rows belong to the unsent new chat whose first message is still on screen.
 */
export function visibleRouteState(
  state: RouteState | null,
  currentSession: ChatSession | null,
  messages: ChatMessage[],
): RouteState | null {
  if (!state) return null;
  if (state.kind === "routed" && state.sessionUuid) {
    return state.sessionUuid === currentSession?.uuid ? state : null;
  }
  if (state.kind !== "routed" && currentSession) return null;
  return messages.some((m) => m.uuid === state.anchorUuid) ? state : null;
}

/** Fire-and-forget: tell the backend what the user did with a routing decision. */
function recordRouteOutcome(
  decisionUuid: string,
  outcome: AgentRouteOutcome,
  agentUuid: string | null | undefined,
  sessionUuid: string | null | undefined,
) {
  void agentService
    .recordRouteOutcome(decisionUuid, { outcome, sessionUuid, agentUuid })
    .catch((err: unknown) => console.warn("Failed to record routing outcome:", err));
}

/**
 * Dependencies injected by the Chat page component.
 */
interface UseChatStreamingDeps {
  /** Current messages list getter (read via callback to avoid stale closures). */
  getMessages: () => ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  currentSession: ChatSession | null;
  setCurrentSession: (session: ChatSession) => void;
  selectedModel: string;
  selectedAgent: string | null;
  selectedBrand: string | null;
  /** Reasoning effort for this chat (null = provider default); sent on create and with agent streams. */
  selectedReasoningEffort: string | null;
  /** Optional project UUID — passed when chat is scoped to a project. */
  projectUuid: string | null;
  modelSupportsVision: boolean;
  /** Navigate to a chat URL. */
  navigate: (path: string, opts?: { replace?: boolean }) => void;
  /** Toast for user-visible errors. */
  showToast: (message: string, type: "error" | "warning" | "success" | "info") => void;
  /** Attachments hook — for uploading images/documents during send. */
  attachments: {
    consumePendingFiles: () => ConsumedFiles;
    downloadSharePointDocuments: (
      files: SharePointItem[],
      updateMeta: (metas: SharePointDocumentMeta[]) => void,
    ) => Promise<SharePointDownloadResult>;
    setIsUploadingImages: (v: boolean) => void;
  };
  /** Sharing hook — for fork-before-send logic. */
  chatSharing: {
    forkIfNeeded: (
      session: ChatSession | null,
      messageContent: string,
      setInputValue: (v: string) => void,
    ) => Promise<"ok" | "forked" | "blocked">;
  };
  inputValue: string;
  setInputValue: (v: string) => void;
  /** The first message of this new chat goes to the auto-router ("Auto" picked, no agent, no chat yet). */
  autoRoute: boolean;
  setSelectedAgent: (uuid: string | null) => void;
  setAutoRoute: (on: boolean) => void;
  /** Reset to a fresh chat, keeping the given project scope. */
  startNewChat: (keepProjectUuid?: string | null) => void;
}

/**
 * Encapsulates all streaming-related state and logic extracted from Chat.tsx.
 *
 * Owns: `isStreaming`, `streamingContent`, `sidebarRefreshTrigger`,
 *       `handleSend()`, `handleRegenerate()`, and the auto-routing row state.
 */
export function useChatStreaming(deps: UseChatStreamingDeps) {
  // NOSONAR -- hook orchestrates streaming lifecycle with inherent state complexity
  const {
    getMessages,
    setMessages,
    currentSession,
    setCurrentSession,
    selectedModel,
    selectedAgent,
    selectedBrand,
    selectedReasoningEffort,
    modelSupportsVision,
    projectUuid,
    navigate,
    showToast,
    attachments,
    chatSharing,
    inputValue,
    setInputValue,
    autoRoute,
    setSelectedAgent,
    setAutoRoute,
    startNewChat,
  } = deps;

  const { trackOperation, updateOperationStatus } = useOperations();
  const { refreshProfile } = useAuth();

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [streamingSources, setStreamingSources] = useState<SourceMetadata[]>([]);
  const [streamingReasoning, setStreamingReasoning] = useState<ReasoningStep[]>([]);
  const [streamingReasoningSummary, setStreamingReasoningSummary] = useState("");
  const [streamingHtmlArtifacts, setStreamingHtmlArtifacts] = useState<StreamingArtifact[]>([]);
  const [streamingFileArtifacts, setStreamingFileArtifacts] = useState<StreamingArtifact[]>([]);

  // ── Ask-user state (structured question card in chat) ───────────────────
  const [askUserQuestions, setAskUserQuestions] = useState<AskUserQuestion[] | null>(null);

  // ── Extension access prompt (opens once the reply completes) ─────────────
  const [extensionAccessRequest, setExtensionAccessRequest] = useState<ExtensionAccessRequest | null>(null);

  // ── The router's mid-chat suggestion of a better agent (MHS-628) ─────────
  const [handoffSuggestion, setHandoffSuggestion] = useState<AgentHandoffSuggestion | null>(null);

  // ── Auto-routing row, and a first message held until the user picks a suggestion ──
  const [routeState, setRouteState] = useState<RouteState | null>(null);
  const pendingRouteSendRef = useRef<{ send: FirstSend; decision: AgentRouteDecision } | null>(null);
  // Bumped by every send, Stop/leaving the chat and unmount: a first send still waiting on the router
  // compares its number after the call and drops itself when it is no longer the latest.
  const sendGenRef = useRef(0);
  useEffect(
    () => () => {
      sendGenRef.current++;
    },
    [],
  );

  // ── Context info state (dev mode context usage panel) ────────────────────
  const [contextInfo, setContextInfo] = useState<ContextInfo | null>(null);

  // ── Operation stream state ──────────────────────────────────────────────
  const [operationId, setOperationId] = useState<string | null>(null);
  // Keep a ref in sync so the event handler can read the current operationId
  // without adding it as a useCallback dependency (which would cause churn).
  const operationIdRef = useRef<string | null>(null);
  operationIdRef.current = operationId;

  // Track operation IDs that have reached a terminal state so we never
  // reconnect to them (belt-and-suspenders guard against the reconnect loop).
  const finishedOpsRef = useRef<Set<string>>(new Set());

  // Refs to avoid stale closures in the onEvent callback. These refs are
  // written before setting the operationId and read inside handleStreamEvent.
  const fullContentRef = useRef("");
  const lastUpdateTimeRef = useRef(0);
  const messageAppendedRef = useRef(false);
  const collectedArtifactsRef = useRef<StreamingArtifact[]>([]);
  const collectedSourcesRef = useRef<SourceMetadata[]>([]);
  const collectedReasoningRef = useRef<ReasoningStep[]>([]);
  const collectedReasoningSummaryRef = useRef("");
  const pendingExtensionAccessRef = useRef<ExtensionAccessRequest | null>(null);
  const sessionRef = useRef<ChatSession | null>(null);
  /** The session_uuid returned in the agent's message_start event (used
   *  when a new session is created on the backend for sessionless agent calls). */
  const pendingSessionUuidRef = useRef<string | null>(null);

  // ── Stream event handler helpers (closures over refs/setters) ───────────
  // Each handler corresponds to one SSE event type.

  function handleMessageStart(data: Record<string, unknown>) {
    const meta = data as { session_uuid?: string };
    if (!meta.session_uuid) return;

    pendingSessionUuidRef.current = meta.session_uuid;
    chatService
      .getSession(meta.session_uuid)
      .then((res) => {
        const session = res.data;
        sessionRef.current = session;
        setCurrentSession(session);
        navigate(buildChatSessionPath(session.uuid, session.projectUuid), { replace: true });
        setSidebarRefreshTrigger((n) => n + 1);
      })
      .catch((err) => {
        console.error("Failed to resolve session from message_start:", err);
      });
  }

  function handleToolStart(data: Record<string, unknown>) {
    const payload = data as unknown as ToolStartEvent;
    setActiveToolName(payload.name);
    collectedReasoningRef.current.push({
      toolCallId: payload.tool_call_id,
      toolName: payload.name,
      toolArgs: typeof payload.arguments === "object" ? JSON.stringify(payload.arguments) : payload.arguments,
    });
    setStreamingReasoning([...collectedReasoningRef.current]);
  }

  function handleReasoningSummary(data: Record<string, unknown>) {
    const { delta } = data as unknown as ReasoningSummaryEvent;
    collectedReasoningSummaryRef.current += delta;
    setStreamingReasoningSummary(collectedReasoningSummaryRef.current);
  }

  function registerStreamedArtifact(
    art: NonNullable<NonNullable<ToolEndEvent["metadata"]>["artifacts"]>[number],
    sessionUuid?: string,
  ) {
    collectedArtifactsRef.current.push({
      filename: art.filename,
      url: art.url,
      is_image: art.is_image,
      mime_type: art.mime_type,
      attachment_uuid: art.attachment_uuid,
      version: art.version,
      lineage_uuid: art.lineage_uuid,
      is_revision: art.is_revision,
      is_html: (art as Record<string, unknown>).is_html as boolean | undefined,
      title: (art as Record<string, unknown>).title as string | undefined,
      html_content: (art as Record<string, unknown>).html_content as string | undefined,
    });

    // Register version info so the Markdown link override can show version
    // badges and history buttons for this attachment.
    if (art.attachment_uuid && art.version && art.lineage_uuid && sessionUuid) {
      registerAttachmentVersion(art.attachment_uuid, {
        version: art.version,
        lineageUuid: art.lineage_uuid,
        isRevision: art.is_revision ?? false,
        sessionUuid,
      });
    }

    // Register the real filename so the Markdown link renderer can decide
    // previewability from the actual file even when the LLM wrote prose link
    // text (e.g. "Open PDF preview").
    if (art.attachment_uuid) {
      registerAttachmentFilename(art.attachment_uuid, art.filename);
    }
  }

  function processToolEndArtifacts(artifacts: NonNullable<NonNullable<ToolEndEvent["metadata"]>["artifacts"]>) {
    const sessionUuid = sessionRef.current?.uuid;
    for (const art of artifacts) {
      registerStreamedArtifact(art, sessionUuid);
    }
    // Surface HTML artifacts for inline card rendering.
    const htmlArts = collectedArtifactsRef.current.filter((a) => a.is_html);
    if (htmlArts.length > 0) {
      setStreamingHtmlArtifacts([...htmlArts]);
    }
    // Surface other generated files (docx/pptx/xlsx/etc.) as artifact cards too,
    // matching the HTML treatment instead of leaving them as a bare markdown link.
    const fileArts = collectedArtifactsRef.current.filter((a) => !a.is_html && !a.is_image);
    if (fileArts.length > 0) {
      setStreamingFileArtifacts([...fileArts]);
    }
  }

  /** The stream's `route_decision` (MHS-627): keeps the routed row, or brings it back after the chat remounts. */
  function handleRouteDecision(data: Record<string, unknown>) {
    const { sessionUuid, ...decision } = data as unknown as AgentRouteDecision & {
      agent: RoutedAgent;
      sessionUuid: string;
    };
    setRouteState((prev) =>
      prev?.kind === "routed" && prev.decision.decisionUuid === decision.decisionUuid
        ? { ...prev, sessionUuid }
        : { kind: "routed", anchorUuid: "", agent: decision.agent, decision, sessionUuid },
    );
  }

  /** Show a handoff (MHS-628): the divider, the new agent in the header, and later sends go to that agent. */
  function applyHandoff(handoff: AgentHandoff, closing: ChatMessage | null) {
    const session = sessionRef.current ?? currentSession;
    if (!session) return;
    setMessages((prev) => {
      if (hasHandoff(prev, handoff.uuid)) return prev; // a reconnect replays the stream's events
      return [...prev, ...(closing ? [closing] : []), makeHandoffMessage(session.id, handoff)];
    });
    setSelectedAgent(handoff.toAgent.uuid);
    const moved = { ...session, agentUuid: handoff.toAgent.uuid, agentName: handoff.toAgent.name };
    sessionRef.current = moved;
    setCurrentSession(moved);
  }

  /** The stream's `handoff`: the first agent's reply so far closes, the new agent's reply starts fresh. */
  function handleHandoff(data: Record<string, unknown>) {
    const handoff = data as unknown as AgentHandoff;
    const session = sessionRef.current;
    const artifacts = [...collectedArtifactsRef.current];
    const sources = [...collectedSourcesRef.current];
    const reasoning = [...collectedReasoningRef.current];
    const reasoningSummary = collectedReasoningSummaryRef.current || undefined;
    let content = sanitizeDownloadLinks(fullContentRef.current, artifacts, sources);
    content = injectMissingArtifacts(content, artifacts);
    const closing =
      session && (content || reasoning.length > 0 || reasoningSummary)
        ? makeAssistantMessage(session.id, content, sources, reasoning, artifacts, reasoningSummary)
        : null;
    applyHandoff(handoff, closing);
    fullContentRef.current = "";
    collectedArtifactsRef.current = [];
    collectedSourcesRef.current = [];
    collectedReasoningRef.current = [];
    collectedReasoningSummaryRef.current = "";
    setStreamingContent("");
    setStreamingReasoning([]);
    setStreamingReasoningSummary("");
    setStreamingSources([]);
    setStreamingHtmlArtifacts([]);
    setStreamingFileArtifacts([]);
    setActiveToolName(null);
  }

  function handleToolEnd(data: Record<string, unknown>) {
    setActiveToolName(null);
    const payload = data as unknown as ToolEndEvent;
    if (payload.metadata?.artifacts) {
      processToolEndArtifacts(payload.metadata.artifacts);
    }
    if (payload.metadata?.sources) {
      for (const src of payload.metadata.sources) {
        collectedSourcesRef.current.push(src);
      }
      setStreamingSources([...collectedSourcesRef.current]);
    }
    if (payload.metadata?.extension_access) {
      pendingExtensionAccessRef.current = payload.metadata.extension_access;
    }
  }

  function handleContentDelta(data: Record<string, unknown>) {
    const delta = (data as { delta: string }).delta;
    fullContentRef.current += delta;
    const now = Date.now();
    if (now - lastUpdateTimeRef.current >= UPDATE_INTERVAL) {
      setStreamingContent(fullContentRef.current);
      lastUpdateTimeRef.current = now;
    }
  }

  function handleMessageEnd(endData?: { tokensUsed?: number; costUsd?: number }) {
    let finalContent = fullContentRef.current;
    finalContent = sanitizeDownloadLinks(finalContent, collectedArtifactsRef.current, collectedSourcesRef.current);
    finalContent = injectMissingArtifacts(finalContent, collectedArtifactsRef.current);
    setStreamingContent(finalContent);

    const session = sessionRef.current;
    const reasoningSummary = collectedReasoningSummaryRef.current || undefined;
    if (session) {
      setMessages((prev) => {
        // Deduplicate: if the last message is an assistant message with the
        // same content (e.g. loaded from DB after navigate-away-and-back),
        // skip appending a duplicate.
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.role === "assistant" && lastMsg.content === finalContent) {
          return prev;
        }
        const msg = makeAssistantMessage(
          session.id,
          finalContent,
          collectedSourcesRef.current,
          collectedReasoningRef.current,
          collectedArtifactsRef.current,
          reasoningSummary,
        );
        // Attach usage data from the message_end event
        if (endData?.tokensUsed) {
          msg.tokensUsed = endData.tokensUsed;
        }
        if (endData?.costUsd) {
          msg.messageMetadata = { ...msg.messageMetadata, cost_usd: endData.costUsd };
        }
        return [...prev, msg];
      });
      messageAppendedRef.current = true;
    }
    setStreamingContent("");

    // Refresh profile so the budget banner updates with the latest spend
    refreshProfile().catch(() => {});
  }

  function handleStreamError(data: Record<string, unknown>, fallbackSessionId: number) {
    console.error("Stream error:", data);
    const errorMsg = (data as { message?: string })?.message || DEFAULT_ERROR_MSG;
    const errorCode = (data as { code?: string })?.code;
    const sessionId = sessionRef.current?.id || fallbackSessionId;
    setMessages((prev) => [...prev, makeErrorMessage(sessionId, errorMsg)]);
    messageAppendedRef.current = true;
    setStreamingContent("");

    // Refresh profile on budget exceeded so the banner updates
    if (errorCode === "budget_exceeded") {
      refreshProfile().catch(() => {});
    }
  }

  /** Flush any buffered content that wasn't finalized by a message_end event. */
  function flushUnfinalizedContent() {
    if (!fullContentRef.current || messageAppendedRef.current) return;

    let finalContent = fullContentRef.current;
    finalContent = sanitizeDownloadLinks(finalContent, collectedArtifactsRef.current, collectedSourcesRef.current);
    finalContent = injectMissingArtifacts(finalContent, collectedArtifactsRef.current);
    const session = sessionRef.current;
    if (session) {
      setMessages((prev) => {
        // Deduplicate: skip if the same content already exists from DB load
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.role === "assistant" && lastMsg.content === finalContent) {
          return prev;
        }
        return [
          ...prev,
          makeAssistantMessage(
            session.id,
            finalContent,
            collectedSourcesRef.current,
            undefined,
            collectedArtifactsRef.current,
          ),
        ];
      });
    }
    setStreamingContent("");
  }

  /** Mark an operation as finished and update the operation status. */
  function markOperationFinished(status: "completed" | "failed") {
    const opId = operationIdRef.current;
    if (opId) {
      finishedOpsRef.current.add(opId);
      updateOperationStatus(opId, status);
    }
  }

  /** Reset all streaming UI state after an operation terminates. */
  function clearStreamingState() {
    setIsStreaming(false);
    setStreamingContent("");
    setActiveToolName(null);
    setOperationId(null);
  }

  function handleOperationCompleted() {
    // NOSONAR — nested function groups related flush-and-finalize logic; extracting would require passing many closure variables
    flushUnfinalizedContent();
    markOperationFinished("completed");
    setIsStreaming(false);
    setActiveToolName(null);
    setOperationId(null);
    setSidebarRefreshTrigger((n) => n + 1);
    setExtensionAccessRequest(pendingExtensionAccessRef.current);
  }

  function handleOperationFailed(data: Record<string, unknown>, fallbackSessionId: number) {
    const errorData = data as { error?: string; code?: string };
    console.error("Chat/Agent operation failed:", errorData.error);

    if (!messageAppendedRef.current) {
      const sessionId = sessionRef.current?.id || fallbackSessionId;
      // Use the actual error message from the backend when available
      const errorMsg = errorData.error || DEFAULT_ERROR_MSG;
      setMessages((prev) => [...prev, makeErrorMessage(sessionId, errorMsg)]);
    }

    // Refresh profile on budget exceeded so the banner updates
    if (errorData.code === "budget_exceeded" || errorData.error?.includes("budget exceeded")) {
      refreshProfile().catch(() => {});
    }

    markOperationFinished("failed");
    clearStreamingState();
  }

  // ── Operation stream event handler ──────────────────────────────────────
  const handleStreamEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;
      const fallbackSessionId = currentSession?.id || 0;

      switch (eventName) {
        case "message_start":
          handleMessageStart(data);
          break;
        case "tool_start":
          handleToolStart(data);
          break;
        case "tool_end":
          handleToolEnd(data);
          break;
        case "reasoning_summary":
          handleReasoningSummary(data);
          break;
        case "route_decision":
          handleRouteDecision(data);
          break;
        case "handoff":
          handleHandoff(data);
          break;
        case "handoff_suggestion":
          setHandoffSuggestion(data as unknown as AgentHandoffSuggestion);
          break;
        case "content_delta":
          handleContentDelta(data);
          break;
        case "message_end":
          handleMessageEnd(data as { tokensUsed?: number; costUsd?: number });
          break;
        case "error":
          handleStreamError(data, fallbackSessionId);
          break;
        case "ask_user": {
          const askData = data as {
            questions?: AskUserQuestion[];
            question?: string;
            options?: string[];
          };
          setAskUserQuestions(askData.questions ?? null);
          // Stop showing tool/streaming indicators — the card replaces them
          setActiveToolName(null);
          setIsStreaming(false);
          break;
        }
        case "context_info":
          setContextInfo(data as unknown as ContextInfo);
          break;
        case "operation_completed":
          handleOperationCompleted();
          break;
        case "operation_failed":
          handleOperationFailed(data, fallbackSessionId);
          break;
      }
    },
    [currentSession, setCurrentSession, setMessages, setSelectedAgent, navigate, updateOperationStatus],
  );

  useOperationStream(operationId, {
    onEvent: handleStreamEvent,
    onError: (err) => {
      console.error("Chat/Agent operation stream error:", err);
      const sessionId = sessionRef.current?.id || currentSession?.id || 0;
      // Use the actual error string from the stream when available
      const errorMsg = err || DEFAULT_ERROR_MSG;
      setMessages((prev) => [...prev, makeErrorMessage(sessionId, errorMsg)]);
      markOperationFinished("failed");
      clearStreamingState();
    },
  });

  // ---------------------------------------------------------------------------
  // Helper to reset refs before starting a new stream
  // ---------------------------------------------------------------------------
  const resetStreamRefs = useCallback(() => {
    fullContentRef.current = "";
    lastUpdateTimeRef.current = Date.now();
    messageAppendedRef.current = false;
    collectedArtifactsRef.current = [];
    collectedSourcesRef.current = [];
    collectedReasoningRef.current = [];
    collectedReasoningSummaryRef.current = "";
    pendingExtensionAccessRef.current = null;
    pendingSessionUuidRef.current = null;
    setStreamingSources([]);
    setStreamingReasoning([]);
    setStreamingReasoningSummary("");
    setStreamingHtmlArtifacts([]);
    setStreamingFileArtifacts([]);
    setAskUserQuestions(null);
    setExtensionAccessRequest(null);
    setHandoffSuggestion(null);
    setContextInfo(null);
  }, []);

  // ---------------------------------------------------------------------------
  // handleSend sub-routines (closures over hook state)
  // ---------------------------------------------------------------------------

  /** Ensure a ChatSession exists, creating one if needed. Returns the session. */
  async function ensureSession(
    session: ChatSession | null,
    agentUuid: string | undefined,
    messageContent: string,
  ): Promise<ChatSession> {
    if (session) return session;

    const sessionRes = await chatService.createSession({
      modelId: selectedModel,
      agentUuid,
      brandUuid: selectedBrand || undefined,
      reasoningEffort: selectedReasoningEffort ?? undefined,
      title: messageContent.slice(0, 50),
      projectUuid: projectUuid || undefined,
    });
    const newSession = sessionRes.data;
    sessionRef.current = newSession;
    setCurrentSession(newSession);
    navigate(buildChatSessionPath(newSession.uuid, newSession.projectUuid), { replace: true });
    setSidebarRefreshTrigger((n) => n + 1);
    return newSession;
  }

  /** Upload image and document attachments, updating the temp user message.
   * Returns `aborted: true` if any batch failed with a 4xx error — the caller
   * should stop the send rather than proceeding without the files. */
  async function uploadAllAttachments(
    session: ChatSession,
    pendingImageFiles: File[],
    pendingDocumentFiles: File[],
    tempMessageUuid: string,
  ): Promise<{ imageUuids: string[] | undefined; documentUuids: string[] | undefined; aborted: boolean }> {
    let imageUuids: string[] | undefined;
    let documentUuids: string[] | undefined;

    if (pendingImageFiles.length > 0 && modelSupportsVision) {
      const result = await uploadAttachmentBatch(
        session.uuid,
        pendingImageFiles,
        "images",
        showToast,
        attachments.setIsUploadingImages,
      );
      imageUuids = result.uuids;
      if (result.attachments) {
        updateMessageAttachments(setMessages, tempMessageUuid, result.attachments, "replace");
      }
      if (result.aborted) {
        return { imageUuids, documentUuids, aborted: true };
      }
    }

    if (pendingDocumentFiles.length > 0) {
      const result = await uploadAttachmentBatch(
        session.uuid,
        pendingDocumentFiles,
        "documents",
        showToast,
        attachments.setIsUploadingImages,
      );
      documentUuids = result.uuids;
      if (result.attachments) {
        updateMessageAttachments(setMessages, tempMessageUuid, result.attachments, "append");
      }
      if (result.aborted) {
        return { imageUuids, documentUuids, aborted: true };
      }
    }

    return { imageUuids, documentUuids, aborted: false };
  }

  /** Execute the agent stream path. */
  async function startAgentStream(
    agentUuid: string,
    session: ChatSession | null,
    messageContent: string,
    pendingImageFiles: File[],
    pendingDocumentFiles: File[],
    tempMessageUuid: string,
    sharepointMetas: SharePointDocumentMeta[] | undefined,
    sharepointContentText: string | undefined,
    sharepointSlideImages: string[] | undefined,
    routeDecisionUuid?: string,
  ) {
    let resolvedSession = session;
    let imageAttachmentUuids: string[] | undefined;
    let documentAttachmentUuids: string[] | undefined;

    const hasFiles = pendingImageFiles.length > 0 || pendingDocumentFiles.length > 0;
    if (hasFiles) {
      resolvedSession = await ensureSession(resolvedSession, agentUuid, messageContent);
      const uploaded = await uploadAllAttachments(
        resolvedSession,
        pendingImageFiles,
        pendingDocumentFiles,
        tempMessageUuid,
      );
      imageAttachmentUuids = uploaded.imageUuids;
      documentAttachmentUuids = uploaded.documentUuids;
      if (uploaded.aborted) {
        // Upload failed with a blocking error (4xx) — don't send the message.
        // Clear streaming state so the UI doesn't get stuck.
        clearStreamingState();
        return;
      }
    }

    const result = await agentService.stream(
      agentUuid,
      messageContent,
      resolvedSession?.uuid,
      sharepointMetas,
      sharepointContentText,
      sharepointSlideImages,
      projectUuid || undefined,
      imageAttachmentUuids,
      documentAttachmentUuids,
      routeDecisionUuid,
      selectedReasoningEffort ?? undefined,
    );

    // If the backend created a new session, resolve it now
    if (result.session_uuid && !resolvedSession) {
      resolvedSession = await resolveNewSession(
        result.session_uuid,
        setCurrentSession,
        navigate,
        setSidebarRefreshTrigger,
        sessionRef,
      );
    }

    setOperationId(result.operation_id);
    trackOperation(
      buildOperationRecord(
        result.operation_id,
        "agent_chat",
        resolvedSession?.uuid || result.session_uuid || "",
        resolvedSession?.title || "Chat",
      ),
    );
  }

  /** Execute the regular chat stream path (no agent). */
  async function startChatStream(
    session: ChatSession | null,
    messageContent: string,
    pendingImageFiles: File[],
    pendingDocumentFiles: File[],
    tempMessageUuid: string,
    sharepointMetas: SharePointDocumentMeta[] | undefined,
    sharepointContentText: string | undefined,
    sharepointSlideImages: string[] | undefined,
  ) {
    const resolvedSession = await ensureSession(session, undefined, messageContent);

    const { imageUuids, documentUuids, aborted } = await uploadAllAttachments(
      resolvedSession,
      pendingImageFiles,
      pendingDocumentFiles,
      tempMessageUuid,
    );
    if (aborted) {
      // Upload failed with a blocking error (4xx) — don't send the message.
      // Clear streaming state so the UI doesn't get stuck.
      clearStreamingState();
      return;
    }

    const result = await chatService.streamMessage(
      resolvedSession.uuid,
      messageContent,
      sharepointMetas,
      sharepointContentText,
      sharepointSlideImages,
      imageUuids,
      documentUuids,
    );

    setOperationId(result.operation_id);
    trackOperation(
      buildOperationRecord(result.operation_id, "chat", resolvedSession.uuid, resolvedSession.title || "Chat"),
    );
  }

  /** Auto mode: ask the router about the first message. A suggestion holds the message until the user picks. */
  async function routeFirstMessage(send: FirstSend, sendGen: number): Promise<AgentRouteDecision | null> {
    setRouteState({ kind: "routing", anchorUuid: send.tempMessageUuid });
    let decision: AgentRouteDecision | null = null;
    try {
      const timeout = AbortSignal.timeout(ROUTE_TIMEOUT_MS);
      decision = (await agentService.route(send.messageContent, projectUuid || undefined, timeout)).data;
    } catch (err) {
      console.warn("Agent routing failed; continuing without an agent:", err);
    }
    if (sendGen !== sendGenRef.current) return null; // stopped or left while routing: the caller drops the send
    if (decision?.decision === "suggest" && decision.candidates.length > 0) {
      pendingRouteSendRef.current = { send, decision };
      setRouteState({ kind: "suggest", anchorUuid: send.tempMessageUuid, decision });
    } else if (decision?.decision === "route" && decision.agent) {
      setSelectedAgent(decision.agent.uuid);
      setRouteState({
        kind: "routed",
        anchorUuid: send.tempMessageUuid,
        agent: decision.agent,
        decision,
        sessionUuid: null,
      });
    } else {
      setRouteState(null);
    }
    return decision;
  }

  /** After an auto-routed first send started: record the outcome and tie a routed row to its new chat. */
  function recordFirstSendOutcome(decision: AgentRouteDecision, routedAgentUuid: string | undefined) {
    const sessionUuid = sessionRef.current?.uuid;
    recordRouteOutcome(decision.decisionUuid, routedAgentUuid ? "routed" : "no_match", routedAgentUuid, sessionUuid);
    if (routedAgentUuid && sessionUuid) {
      setRouteState((prev) => (prev?.kind === "routed" ? { ...prev, sessionUuid } : prev));
    }
  }

  /** New chat: clear the previous routing row and, in Auto mode, route the first message (or hold it). */
  async function prepareFirstSend(
    send: FirstSend,
    agentUuid: string | undefined,
    sendGen: number,
  ): Promise<{ held: boolean; agentUuid: string | undefined; decision: AgentRouteDecision | null }> {
    setRouteState(null);
    pendingRouteSendRef.current = null;
    if (agentUuid || !autoRoute) return { held: false, agentUuid, decision: null };
    const decision = await routeFirstMessage(send, sendGen);
    const routedAgentUuid = decision?.decision === "route" ? decision.agent?.uuid : undefined;
    return { held: pendingRouteSendRef.current !== null, agentUuid: routedAgentUuid, decision };
  }

  // ---------------------------------------------------------------------------
  // handleSend — the main send flow
  // ---------------------------------------------------------------------------

  /** Handle the user's response to an ask_user question card.
   *  Clears the card, sets the answer as input, and triggers send. */
  const handleAskUserSubmit = useCallback(
    (text: string) => {
      // Defer the card removal and send to the next microtask so the
      // browser's form submit event completes before the DOM unmounts,
      // avoiding "Form submission canceled because the form is not connected".
      queueMicrotask(() => {
        setAskUserQuestions(null);
        setInputValue(text);
        pendingSendRef.current = true;
      });
    },
    [setInputValue],
  );

  /** After the user turns on the extension the assistant asked for, re-send their last message. */
  const handleExtensionAccessTurnedOn = useCallback(() => {
    setExtensionAccessRequest(null);
    const lastUserMessage = [...getMessages()].reverse().find((m) => m.role === "user");
    if (!lastUserMessage?.content.trim()) return;
    setInputValue(lastUserMessage.content);
    pendingSendRef.current = true;
  }, [getMessages, setInputValue]);

  // When pendingSend is true, trigger handleSend on the next render
  const pendingSendRef = useRef(false);

  const handleSend = useCallback(async () => {
    // NOSONAR -- send flow requires sequential steps with branching for agent vs chat paths
    if (!inputValue.trim() || isStreaming) return;
    if (visibleRouteState(routeState, currentSession, getMessages())?.kind === "suggest") return;
    const sendGen = ++sendGenRef.current;

    const messageContent = inputValue;
    const {
      imageFiles: pendingImageFiles,
      documentFiles: pendingDocumentFiles,
      sharepointFiles: pendingSharepointFiles,
    } = attachments.consumePendingFiles();
    setInputValue("");

    // If this is a shared session with read_write, fork it first
    const forkResult = await chatSharing.forkIfNeeded(currentSession, messageContent, setInputValue);
    if (forkResult !== "ok") return;

    // Add user message to UI immediately — before any downloads
    const preliminaryMetas = buildPreliminaryMetas(pendingSharepointFiles);
    const tempUserMessage = makeTempUserMessage(currentSession?.id || 0, messageContent, preliminaryMetas);
    setMessages((prev) => [...prev, tempUserMessage]);

    // Download SharePoint documents (user message is already visible)
    let sharepointMetas: SharePointDocumentMeta[] | undefined;
    let sharepointContentText: string | undefined;
    let sharepointSlideImages: string[] | undefined;
    if (pendingSharepointFiles.length > 0) {
      const result = await attachments.downloadSharePointDocuments(pendingSharepointFiles, (metas) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.uuid === tempUserMessage.uuid ? { ...m, messageMetadata: { sharepoint_documents: metas } } : m,
          ),
        );
      });
      sharepointMetas = result.metas;
      sharepointContentText = result.contentText;
      sharepointSlideImages = result.slideImages;
    }

    try {
      setIsStreaming(true);
      setStreamingContent("");
      resetStreamRefs();

      const session = currentSession;
      let agentUuid = selectedAgent || session?.agentUuid;
      sessionRef.current = session;

      let decision: AgentRouteDecision | null = null;
      if (!session) {
        const first = await prepareFirstSend(
          {
            messageContent,
            imageFiles: pendingImageFiles,
            documentFiles: pendingDocumentFiles,
            tempMessageUuid: tempUserMessage.uuid,
            sharepointMetas,
            sharepointContentText,
            sharepointSlideImages,
          },
          agentUuid,
          sendGen,
        );
        if (sendGen !== sendGenRef.current) return;
        if (first.held) {
          setIsStreaming(false);
          return;
        }
        agentUuid = first.agentUuid;
        decision = first.decision;
      }

      if (agentUuid) {
        await startAgentStream(
          agentUuid,
          session,
          messageContent,
          pendingImageFiles,
          pendingDocumentFiles,
          tempUserMessage.uuid,
          sharepointMetas,
          sharepointContentText,
          sharepointSlideImages,
          decision?.decision === "route" ? decision.decisionUuid : undefined,
        );
      } else {
        await startChatStream(
          session,
          messageContent,
          pendingImageFiles,
          pendingDocumentFiles,
          tempUserMessage.uuid,
          sharepointMetas,
          sharepointContentText,
          sharepointSlideImages,
        );
      }

      if (decision) recordFirstSendOutcome(decision, agentUuid);
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages((prev) => [...prev, makeErrorMessage(currentSession?.id || 0, DEFAULT_ERROR_MSG)]);
      setIsStreaming(false);
      setStreamingContent("");
      setActiveToolName(null);
    }
  }, [
    inputValue,
    isStreaming,
    currentSession,
    selectedAgent,
    selectedModel,
    selectedReasoningEffort,
    modelSupportsVision,
    projectUuid,
    attachments,
    chatSharing,
    setInputValue,
    setMessages,
    setCurrentSession,
    navigate,
    showToast,
    getMessages,
    resetStreamRefs,
    trackOperation,
    autoRoute,
    setSelectedAgent,
    routeState,
  ]);

  // ---------------------------------------------------------------------------
  // Auto-send after ask_user card submission or the extension access retry
  // ---------------------------------------------------------------------------

  // Both set the input value and flag a pending send.  We use useEffect to
  // fire handleSend after the state update propagates so handleSend reads
  // the new inputValue.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pendingSendRef.current && inputValue.trim()) {
      pendingSendRef.current = false;
      handleSend();
    }
  });

  // ---------------------------------------------------------------------------
  // handleRegenerate — re-stream a previous assistant response
  // ---------------------------------------------------------------------------

  const handleRegenerate = useCallback(
    async (messageIndex: number) => {
      if (!currentSession || isStreaming) return;

      const messages = getMessages();
      // Find the user message that prompted this assistant response
      const previousUserMessage = messages
        .slice(0, messageIndex)
        .reverse()
        .find((m) => m.role === "user");

      if (!previousUserMessage) {
        console.error("Could not find previous user message");
        return;
      }

      // Remove the assistant message being regenerated and all messages after it
      const newMessages = messages.slice(0, messageIndex);
      setMessages(newMessages);

      try {
        setIsStreaming(true);
        setStreamingContent("");
        resetStreamRefs();
        sessionRef.current = currentSession;

        // Start chat stream via OperationManager
        const result = await chatService.streamMessage(currentSession.uuid, previousUserMessage.content);
        setOperationId(result.operation_id);
        trackOperation(
          buildOperationRecord(result.operation_id, "chat", currentSession.uuid, currentSession.title || "Chat"),
        );
        // Note: isStreaming is cleared by the operation_completed/operation_failed event handler
      } catch (err) {
        console.error("Failed to regenerate message:", err);
        setMessages((prev) => [
          ...prev,
          makeErrorMessage(
            currentSession.id,
            "Sorry, an error occurred while regenerating the message. Please try again.",
          ),
        ]);
        setIsStreaming(false);
        setStreamingContent("");
      }
    },
    [currentSession, isStreaming, getMessages, setMessages, resetStreamRefs, trackOperation],
  );

  // ---------------------------------------------------------------------------
  // reconnectToOperation — reconnect to an active operation (e.g. after nav)
  // ---------------------------------------------------------------------------
  const reconnectToOperation = useCallback(
    (opId: string) => {
      if (operationId === opId) return; // Already connected
      // Never reconnect to an operation we've already seen complete/fail
      if (finishedOpsRef.current.has(opId)) return;
      resetStreamRefs();
      sessionRef.current = currentSession;
      setIsStreaming(true);
      setStreamingContent("");
      setOperationId(opId);
    },
    [operationId, currentSession, resetStreamRefs],
  );

  // ---------------------------------------------------------------------------
  // disconnectStream — stop the active stream and clear streaming UI state.
  // Called when the user navigates to a different session mid-stream.
  // ---------------------------------------------------------------------------
  const disconnectStream = useCallback(() => {
    sendGenRef.current++;
    setRouteState((prev) => (prev?.kind === "routing" ? null : prev));
    const opId = operationIdRef.current;
    if (opId) {
      finishedOpsRef.current.add(opId);
      updateOperationStatus(opId, "completed");
    }
    setIsStreaming(false);
    setStreamingContent("");
    setActiveToolName(null);
    setOperationId(null);
    resetStreamRefs();
  }, [resetStreamRefs, updateOperationStatus]);

  // ---------------------------------------------------------------------------
  // Auto-routing actions (MHS-627)
  // ---------------------------------------------------------------------------

  /** The user answered the suggestions: stream the held first message to that agent, or to plain chat. */
  const resolveSuggestion = useCallback(
    async (agentUuid: string | null) => {
      const pending = pendingRouteSendRef.current;
      if (!pending || isStreaming) return;
      pendingRouteSendRef.current = null;
      setRouteState(null);
      setSelectedAgent(agentUuid);
      const { send } = pending;
      try {
        setIsStreaming(true);
        setStreamingContent("");
        resetStreamRefs();
        sessionRef.current = currentSession;
        if (agentUuid) {
          await startAgentStream(
            agentUuid,
            null,
            send.messageContent,
            send.imageFiles,
            send.documentFiles,
            send.tempMessageUuid,
            send.sharepointMetas,
            send.sharepointContentText,
            send.sharepointSlideImages,
          );
        } else {
          await startChatStream(
            null,
            send.messageContent,
            send.imageFiles,
            send.documentFiles,
            send.tempMessageUuid,
            send.sharepointMetas,
            send.sharepointContentText,
            send.sharepointSlideImages,
          );
        }
        recordRouteOutcome(
          pending.decision.decisionUuid,
          agentUuid ? "suggestion_accepted" : "suggestion_declined",
          agentUuid,
          sessionRef.current?.uuid,
        );
      } catch (err) {
        console.error("Failed to send message:", err);
        setMessages((prev) => [...prev, makeErrorMessage(0, DEFAULT_ERROR_MSG)]);
        setIsStreaming(false);
        setStreamingContent("");
        setActiveToolName(null);
      }
    },
    [
      isStreaming,
      currentSession,
      selectedModel,
      selectedReasoningEffort,
      selectedBrand,
      modelSupportsVision,
      projectUuid,
      attachments,
      setSelectedAgent,
      setMessages,
      setCurrentSession,
      navigate,
      showToast,
      resetStreamRefs,
      trackOperation,
    ],
  );

  /** "Not right? Switch agent": record the override, then re-send the first message in a new chat with that agent. */
  const switchRoutedAgent = useCallback(
    (agentUuid: string | null) => {
      if (routeState?.kind !== "routed" || !routeState.sessionUuid) return; // not before the routed chat exists
      const firstMessage = getMessages().find((m) => m.role === "user")?.content;
      if (!firstMessage?.trim()) return;
      recordRouteOutcome(routeState.decision.decisionUuid, "overridden", agentUuid, routeState.sessionUuid);
      if (isStreaming) disconnectStream();
      setRouteState(null);
      startNewChat(projectUuid);
      setSelectedAgent(agentUuid);
      setAutoRoute(false);
      setInputValue(firstMessage);
      pendingSendRef.current = true;
    },
    [
      routeState,
      getMessages,
      isStreaming,
      disconnectStream,
      startNewChat,
      projectUuid,
      setSelectedAgent,
      setAutoRoute,
      setInputValue,
    ],
  );

  /** The user moves the chat to another agent mid-conversation, or takes the router's suggestion (MHS-628). */
  const switchAgent = useCallback(
    async (agentUuid: string, suggestionUuid?: string) => {
      const session = currentSession;
      if (!session) return;
      try {
        const res = await chatService.handoff(session.uuid, { agentUuid, suggestionUuid });
        sessionRef.current = session;
        applyHandoff(res.data, null);
        setHandoffSuggestion(null);
      } catch (err) {
        showToast(err instanceof Error ? err.message : "Couldn't switch agents. Please try again.", "error");
      }
    },
    [currentSession, showToast, setMessages, setSelectedAgent, setCurrentSession],
  );

  /** The header picker in an open chat: picking another agent moves the chat to it. */
  const selectAgentMidChat = useCallback(
    (agentUuid: string | null) => {
      if (agentUuid && agentUuid !== selectedAgent) void switchAgent(agentUuid);
    },
    [selectedAgent, switchAgent],
  );

  const acceptHandoffSuggestion = useCallback(() => {
    if (handoffSuggestion) void switchAgent(handoffSuggestion.agent.uuid, handoffSuggestion.uuid);
  }, [handoffSuggestion, switchAgent]);

  return {
    isStreaming,
    streamingContent,
    activeToolName,
    streamingSources,
    streamingReasoning,
    streamingReasoningSummary,
    streamingHtmlArtifacts,
    streamingFileArtifacts,
    sidebarRefreshTrigger,
    operationId,
    askUserQuestions,
    contextInfo,
    dismissAskUser: () => setAskUserQuestions(null),
    handleAskUserSubmit,
    extensionAccessRequest,
    dismissExtensionAccess: () => setExtensionAccessRequest(null),
    handleExtensionAccessTurnedOn,
    handleSend,
    handleRegenerate,
    reconnectToOperation,
    disconnectStream,
    routeState,
    resolveSuggestion,
    switchRoutedAgent,
    switchAgent,
    selectAgentMidChat,
    handoffSuggestion,
    acceptHandoffSuggestion,
    dismissHandoffSuggestion: () => setHandoffSuggestion(null),
  };
}
